import React, { Suspense, lazy, useLayoutEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import HomePage from './Pages/Home';
import Hero from './Components/Hero';
import ArticlePage from './Pages/Articles/ArticlePage';
import EventPage from './Pages/Event/EventPage';
import EntityProfilePage from './Pages/Entity/EntityProfilePage';
import Footer from './Components/Footer';
import AboutPage from './Pages/AboutPage';
import SubmitPage from './Pages/SubmitPage';
import SupportPage from './Pages/SupportPage';
import PastorsList from './Pages/Church/PastorsList';
import WealthiestPage from './Pages/Wealthiest/WealthiestPage';
import RepresentativesPage from './Pages/Representatives/RepresentativesPage';
import RepProfile from './Pages/Representatives/RepProfilePage';
import TrumpPage from './Pages/Trump/TrumpPage';
import Header from './Components/Layout/Header';
import { HERO_COPY } from './Components/Layout/nav';
import Cursor from './Components/Fx/Cursor';
import { gsap, ScrollTrigger, prefersReducedMotion } from './lib/gsap';

// Three.js is the heaviest dependency; keep it out of the critical path.
const SentinelField = lazy(() => import('./Components/Fx/SentinelField'));

function App() {
   const location = useLocation();
   const heroCopy = HERO_COPY[location.pathname];
   const main = useRef<HTMLDivElement>(null);

   // Route transition: snap to top, then rise the new page in.
   useLayoutEffect(() => {
      window.scrollTo(0, 0);
      ScrollTrigger.refresh();
      if (prefersReducedMotion() || !main.current) return;
      const tween = gsap.fromTo(
         main.current,
         { autoAlpha: 0, y: 24, filter: 'blur(6px)' },
         { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 0.9, clearProps: 'filter,transform' }
      );
      return () => {
         tween.kill();
      };
   }, [location.pathname]);

   return (
      <>
         <Suspense fallback={null}>
            <SentinelField mode={heroCopy?.size === 'xl' ? 'hero' : 'dim'} />
         </Suspense>
         <div className="grain" aria-hidden="true" />
         <Cursor />
         <Header />

         <div ref={main} className="relative z-10">
            {heroCopy && <Hero copy={heroCopy} />}

            <main>
               <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/event/:eventId" element={<EventPage />} />
                  <Route path="/article/:articleId" element={<ArticlePage />} />
                  <Route path="/submit" element={<SubmitPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/church" element={<PastorsList />} />
                  <Route path="/wealthiest" element={<WealthiestPage />} />
                  <Route path="/profile/:entityId" element={<EntityProfilePage />} />
                  <Route path="/reps" element={<RepresentativesPage />} />
                  <Route path="/reps/:repId" element={<RepProfile />} />
                  <Route path="/trump" element={<TrumpPage />} />
               </Routes>
            </main>

            <Footer />
         </div>
      </>
   );
}

export default App;
